import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Button, TextInput } from 'components/common'
import { Dropdown } from 'components/common'
import { PAGE_PURCHASE_TIMER } from 'utils/constants'
import { updatePurchaseTimer } from 'utils/api/purchaseTimerApi'
import toast from 'react-hot-toast'
import { convertTo12HourFormat } from 'utils/helpers'
import { PURCHASE_TIMER_FORM_FIELDS } from './utils'

const {
  BARCODE,
  STORE,
  AVAILABLE_TO_PURCHASE_TIMER,
  PURCHASE_START_TIME,
  PURCHASE_END_TIME,
} = PURCHASE_TIMER_FORM_FIELDS

type Props = {
  purchaseTimer: any
}

const PurchaseTimerUpdateForm = ({ purchaseTimer }: Props) => {
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      barcode: purchaseTimer.barcode,
      store: purchaseTimer.store,
      availableToPurchaseTimer: purchaseTimer.availableToPurchaseTimer,
      purchaseStartTime: purchaseTimer.purchaseStartTime,
      purchaseEndTime: purchaseTimer.purchaseEndTime,
      isImported: purchaseTimer.isImported ? 'Yes' : 'No',
      createdAt: convertTo12HourFormat(purchaseTimer.createdAt),
      updatedAt: convertTo12HourFormat(purchaseTimer.updatedAt),
    },
  })

  const backToPurchaseTimer = () => {
    navigate(PAGE_PURCHASE_TIMER.path)
  }

  const handleUpdate = async (data: any) => {
    const toastId = toast.loading('Updating Purchase Timer...')
    try {
      await updatePurchaseTimer(purchaseTimer._id, {
        availableToPurchaseTimer: data.availableToPurchaseTimer,
        purchaseStartTime: data.purchaseStartTime,
        purchaseEndTime: data.purchaseEndTime,
      })
      toast.success('Purchase Timer updated successfully!', { id: toastId })
      reset(data)
      backToPurchaseTimer()
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.msg
      toast.error(msg || 'Failed to update Purchase Timer.', { id: toastId })
    }
  }

  return (
    <div className="rounded-lg border bg-white px-5 py-3">
      <div className="mx-auto max-w-5xl">
        <form onSubmit={handleSubmit(handleUpdate)}>
          <div className="grid grid-cols-1 gap-x-14 gap-y-3 md:grid-cols-3">
            <TextInput
              register={register}
              name={BARCODE.key}
              label={BARCODE.label}
              disabled
            />

            <TextInput
              register={register}
              name={STORE.key}
              label={STORE.label}
              disabled
            />

            <Dropdown
              name={AVAILABLE_TO_PURCHASE_TIMER.key}
              label={AVAILABLE_TO_PURCHASE_TIMER.label}
              setValue={setValue}
              defaultValue={purchaseTimer.availableToPurchaseTimer}
              error={
                errors?.[AVAILABLE_TO_PURCHASE_TIMER.key]?.message as string
              }
              options={[
                { label: 'Yes', value: true, disabled: false },
                { label: 'No', value: false, disabled: false },
              ]}
            />

            <TextInput
              register={register}
              name={PURCHASE_START_TIME.key}
              label={PURCHASE_START_TIME.label}
              type="time"
              hintText="GST time, 24h format (e.g. 09:00)"
            />

            <TextInput
              register={register}
              name={PURCHASE_END_TIME.key}
              label={PURCHASE_END_TIME.label}
              type="time"
              hintText="GST time, must be after start time"
            />

            <TextInput
              register={register}
              name="isImported"
              label="Imported"
              disabled
            />

            <TextInput
              register={register}
              name="createdAt"
              label="Created At"
              disabled
            />

            <TextInput
              register={register}
              name="updatedAt"
              label="Updated At"
              disabled
            />
          </div>

          <div className="my-4 flex gap-5">
            <Button buttonText="Save" className="w-32" />
            <Button
              buttonText="Cancel"
              variant="outline"
              type="button"
              onClick={backToPurchaseTimer}
              className="w-32"
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default PurchaseTimerUpdateForm
